import * as React from 'react';
import {useSelector} from 'react-redux';
import useAgencyConfig from 'src/hooks/useAgencyConfig';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useHistory} from 'src/rerouter/index';
import {useEnvironmentContext} from 'src/hooks/useEnvironmentContext';
import {useWebAndExtRouterControl} from 'src/components/micro-apps-extension/hooks/useWebAndExtRouterControl';
import {useReleaseFlag} from 'src/hooks/product-flags';
import {useExtensionProductsList} from './hooks/useExtensionProductsList';
import {SideMenuLink} from '@spaced-out/ui-design-system/lib/components/SideMenuLink';
import SenseLogo from 'src/images/sense-new-logo.svg';
import SupportLinks from './support-links';
import {selectShowBadge} from 'src/selectors/notifications';
import {classify} from 'src/utils/classify';
import css from './extension-side-nav.css';


const routeToProduct = {
  broadcast: '/messages/hv-broadcasts',
  liveAgentTransfer: '/messages/queues',
  messaging: '/messages',
  contacts: '/contacts',
  referral: '/referralv2?source=ext',
  senseai: '/sense-ai-panel?source=ext',
  meetings: '/meetings',
  jobs: '/jobs',
  voiceflow: '/voiceflow',
  notifications: '/notifications',
};

const ExtensionSideNav = () => {
  const router = useHistory();
  const {changeLocation} = useWebAndExtRouterControl();
  const {t} = useLabelTranslation();
  const unreadNotificationCount = useSelector(selectShowBadge);

  const {isExtension, isClientIframe} = useEnvironmentContext();
  const isNudgeNotificationEnabled = useReleaseFlag('enableNudgeNotification');
  const isMicroAppsEnabled = useReleaseFlag(
    'messagingEnableMicroappsInExtension',
  );

  const isRecruiterHubV2Enabled = Boolean(
    useAgencyConfig('discoverRecruiterHubV2Enabled'),
  );
  const jobsRoute = isRecruiterHubV2Enabled
    ? '/recruiter-hub-v2/pipeline'
    : routeToProduct.jobs;

  // This is temporary change and should be removed once recruiter hub is production ready
  routeToProduct.jobs = jobsRoute;

  // useExtensionProductsList checks permissions for products and provides list of product route names
  // ex: messaging, referrals, senseai which are part of routeToProduct
  const {productsList} = useExtensionProductsList();

  // we need this logic to redirect to a product with external clicks from ATS
  const pathName = router.location.pathname;
  const selectedProductKey =
    Object.keys(routeToProduct).find((route) =>
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly broadcast: "/messages/hv-broadcasts"; readonly liveAgentTransfer: "/messages/queues"; readonly messaging: "/messages"; readonly contacts: "/contacts"; readonly referral: "/referralv2?source=ext"; readonly senseai: "/sense-ai-panel?source=ext"; readonly meetings: "/meetings"; readonly jobs: "/jobs/all"; re...'.
      pathName.includes(routeToProduct[route].split('?')[0]),
    ) || '';

  const [selectedProduct, setSelectedProduct] =
    React.useState(selectedProductKey);

  const routeToLocation = (key: string) => {
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly broadcast: "/messages/hv-broadcasts"; readonly liveAgentTransfer: "/messages/queues"; readonly messaging: "/messages"; readonly contacts: "/contacts"; readonly referral: "/referralv2?source=ext"; readonly senseai: "/sense-ai-panel?source=ext"; readonly meetings: "/meetings"; readonly jobs: "/jobs/all"; re...'.
    changeLocation(router, routeToProduct[key]);
  };

  const customTitleMap = {
    messaging: t('Messaging', 'Messaging'),
    broadcast: t('Broadcast', 'Broadcast'),
    contacts: t('Contacts', 'Contacts'),
    liveAgentTransfer: t('Live_Agent_Transfer', 'Live Agent Transfer'),
    referral: t('Referrals', 'Referrals'),
    senseai: t('AI_Copilot', 'AI Copilot'),
    meetings: t('Scheduling', 'Scheduling'),
    jobs: t('Jobs', 'Jobs'),
    screenedCandidates: t('Screened_Candidates', 'Screened Candidates'),
    notifications: t('Notifications', 'Notifications'),
  } as const;

  React.useEffect(() => {
    setSelectedProduct(selectedProductKey);

    if (isExtension && !selectedProductKey) {
      const firstProductInTheList = productsList.length > 0 && productsList[0];
      if (firstProductInTheList) {
        setSelectedProduct(firstProductInTheList);
        routeToLocation(firstProductInTheList);
      }
    }
  }, [selectedProductKey]);

  const handleOnChange = (
    e: React.SyntheticEvent<HTMLElement>,
    selectedKey: string,
  ) => {
    setSelectedProduct(selectedKey);
    routeToLocation(selectedKey);
  };

  const hideExtensionSideNav =
    !isMicroAppsEnabled || !(isExtension || isClientIframe);

  return (
    <>
      {!hideExtensionSideNav ? (
        <div className={css.container}>
          <div className={css.senseLogo}>
            <SenseLogo />
          </div>
          <div className={css.sideNavLinksContainer}>
            <div className={css.sideNavLinks}>
              {productsList.map((product) => {
                return (
                  <SideMenuLink
                    onChange={handleOnChange}
                    pageNameKey={product}
                    selectedValue={selectedProduct}
                    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly messaging: string; readonly broadcast: string; readonly contacts: string; readonly liveAgentTransfer: string; readonly referral: string; readonly senseai: string; readonly meetings: string; readonly jobs: string; readonly screenedCandidates: string; }'.
                    customTitle={customTitleMap[product]}
                    tooltip={{classNames: {tooltip: css.toolTip}}}
                    classNames={{
                      wrapper: classify({
                        [css.selectedDashboardLink]:
                          selectedProduct === product,
                      }),
                    }}
                  />
                );
              })}
            </div>
            <div className={css.bottomLinks}>
              {isNudgeNotificationEnabled && (
                <SideMenuLink
                  pageNameKey="notifications"
                  selectedValue={selectedProduct}
                  customTitle={t('Notifications', 'Notifications')}
                  tooltip={{classNames: {tooltip: css.toolTip}}}
                  classNames={{
                    wrapper: classify({
                      [css.selectedDashboardLink]:
                        selectedProduct === 'notifications',
                    }),
                  }}
                  onChange={handleOnChange}
                  status={unreadNotificationCount ? 'information' : undefined}
                />
              )}
              <SupportLinks />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default ExtensionSideNav;
